import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {CSSProperties, createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from 'react';

export const DEFAULT_ICON_COLOR = '#ff5e1f';

export const FOLDER_COLOR_PALETTE = [
  '#3b5bdb',
  '#15aabf',
  '#fd7e14',
  '#e64980',
  '#be4bdb',
  '#4c6ef5',
  '#12b886',
  '#fab005',
  '#f03e3e',
  '#7950f2',
  '#339af0',
  '#40c057',
  '#fcc419',
  '#ff6b6b',
  '#cc5de8',
  '#74c0fc',
  '#94d82d',
  '#ffe066',
  '#ffa8a8',
  '#e599f7',
  '#ff5e1f',
];

type IconThemeValue = {
  color: string;
  tint: boolean;
  setColor: (color: string) => void;
  setTint: (tint: boolean) => void;
  setTheme: (color: string, tint: boolean) => void;
};

const IconThemeContext = createContext<IconThemeValue | null>(null);

function mixHex(hex: string, withHex: string, amount: number): string {
  const a = toRgb(hex);
  const b = toRgb(withHex);
  const mix = (left: number, right: number) =>
    Math.round(left + (right - left) * amount);
  return rgbToHex(mix(a[0], b[0]), mix(a[1], b[1]), mix(a[2], b[2]));
}

function toRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map(ch => ch + ch)
          .join('')
      : value.padEnd(6, '0').slice(0, 6);
  return [
    parseInt(full.slice(0, 2), 16) || 0,
    parseInt(full.slice(2, 4), 16) || 0,
    parseInt(full.slice(4, 6), 16) || 0,
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map(n => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0'))
      .join('')
  );
}

function hueFromHex(hex: string): number {
  const [r, g, b] = toRgb(hex).map(n => n / 255) as [
    number,
    number,
    number,
  ];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d === 0) return 0;
  let h = 0;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return h;
}

export function folderColorStyle(
  hex?: string | null,
): CSSProperties | undefined {
  if (!hex) return undefined;
  return {
    ['--cd-folder' as string]: hex,
    ['--cd-folder-tab' as string]: mixHex(hex, '#000000', 0.18),
    ['--cd-folder-line' as string]: mixHex(hex, '#ffffff', 0.55),
  };
}

export function applyIconThemeToDom(color: string, tint: boolean) {
  const root = document.documentElement;
  const tab = mixHex(color, '#000000', 0.18);
  const line = mixHex(color, '#ffffff', 0.55);
  root.style.setProperty('--cd-folder', color);
  root.style.setProperty('--cd-folder-tab', tab);
  root.style.setProperty('--cd-folder-line', line);
  const hue = Math.round(hueFromHex(color) - hueFromHex('#f6d155'));
  root.style.setProperty('--cd-icon-hue', `${hue}deg`);
  root.setAttribute('data-cd-icon-tint', tint ? '1' : '0');
}

function readInitial(): {color: string; tint: boolean} {
  const settings = getBootstrapData().settings as
    | {drive?: {folder_color?: string; icon_tint?: boolean}}
    | undefined;
  const color = settings?.drive?.folder_color || DEFAULT_ICON_COLOR;
  const tint = Boolean(settings?.drive?.icon_tint);
  return {color, tint};
}

export function IconThemeProvider({children}: {children: ReactNode}) {
  const [{color, tint}, setState] = useState(readInitial);

  const apply = useCallback((nextColor: string, nextTint: boolean) => {
    applyIconThemeToDom(nextColor, nextTint);
    setState({color: nextColor, tint: nextTint});
  }, []);

  useEffect(() => {
    applyIconThemeToDom(color, tint);
  }, [color, tint]);

  const value = useMemo<IconThemeValue>(
    () => ({
      color,
      tint,
      setColor: next => apply(next, tint),
      setTint: next => apply(color, next),
      setTheme: apply,
    }),
    [apply, color, tint],
  );

  return (
    <IconThemeContext.Provider value={value}>
      {children}
    </IconThemeContext.Provider>
  );
}

export function useIconTheme() {
  const value = useContext(IconThemeContext);
  if (!value) {
    return {
      color: DEFAULT_ICON_COLOR,
      tint: false,
      setColor: () => {},
      setTint: () => {},
      setTheme: () => {},
    };
  }
  return value;
}
